import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import {
  PaperAirplaneIcon,
  PhotoIcon,
  PaperClipIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
  ArchiveBoxIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FaceSmileIcon,
  PhoneIcon,
  PhoneXMarkIcon,
  MicrophoneIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  UserGroupIcon,
  MegaphoneIcon,
  PlusIcon,
  UsersIcon,
  BuildingOfficeIcon,
  TrashIcon,
  Cog6ToothIcon,
  UserMinusIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { PhoneIcon as PhoneIconSolid } from '@heroicons/react/24/solid';
import { CheckIcon as CheckIconSolid, ArchiveBoxIcon as ArchiveBoxIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SOCKET_URL = API_URL.replace('/api', '');

// WebRTC ICE Servers configuration
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]
};

const ChatPage = () => {
  const [socket, setSocket] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [expandedDepts, setExpandedDepts] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typing, setTyping] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'recent', 'archived', 'rooms', 'broadcast'
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');
  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Chat Rooms state
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomMessages, setRoomMessages] = useState([]);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomDescription, setNewRoomDescription] = useState('');
  const [selectedRoomMembers, setSelectedRoomMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Broadcast state
  const [broadcasts, setBroadcasts] = useState([]);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastTarget, setBroadcastTarget] = useState('all');
  const [broadcastDeptId, setBroadcastDeptId] = useState('');
  const [broadcastContent, setBroadcastContent] = useState('');
  const [broadcastPriority, setBroadcastPriority] = useState('normal');

  // Conference call state
  const [conferenceActive, setConferenceActive] = useState(false);
  const [conferenceParticipants, setConferenceParticipants] = useState([]);
  const [incomingConference, setIncomingConference] = useState(null);
  const [conferenceRoomId, setConferenceRoomId] = useState(null);
  const conferencePeersRef = useRef({});
  const conferenceActiveRef = useRef(false);
  const selectedRoomRef = useRef(null);
  const conferenceAudiosRef = useRef({});

  // Room management state
  const [showRoomSettingsModal, setShowRoomSettingsModal] = useState(false);
  const [roomDetails, setRoomDetails] = useState(null);
  const [addMemberIds, setAddMemberIds] = useState([]);

  // Room audio state - always-on voice for rooms
  const [roomAudioActive, setRoomAudioActive] = useState(false);
  const [roomMicMuted, setRoomMicMuted] = useState(true); // Default muted
  const [roomSpeakerMuted, setRoomSpeakerMuted] = useState(false); // Default unmuted so users can hear
  const roomStreamRef = useRef(null);
  const roomAudioActiveRef = useRef(false);
  const roomSpeakerMutedRef = useRef(false); // Track speaker mute state for async handlers

  // Eğlenceli emoji kategorileri
  const emojiCategories = {
    'Yüzler 😊': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐'],
    'El İşaretleri 👋': ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🖤'],
    'Kalpler ❤️': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '♥️'],
    'Eğlence 🎉': ['🎉', '🎊', '🎈', '🎁', '🎀', '🏆', '🥇', '🥈', '🥉', '⚽', '🏀', '🎮', '🎯', '🎲', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎵', '🎶', '🎸', '🎹', '🥁', '🎺', '🎷'],
    'Hayvanlar 🐶': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊'],
    'Yiyecek 🍔': ['🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🌽', '🥕', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕', '🥪', '🥙', '🧆', '🌮', '🌯', '🥗', '🥘', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🌰', '🥜', '🍯', '☕', '🍵', '🧃', '🥤', '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹', '🧉', '🍾'],
    'Doğa 🌸': ['🌸', '💮', '🏵️', '🌹', '🥀', '🌺', '🌻', '🌼', '🌷', '🌱', '🌲', '🌳', '🌴', '🌵', '🌾', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🍄', '🌰', '🦀', '🐚', '🌍', '🌎', '🌏', '🌐', '🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘', '🌙', '🌚', '🌛', '🌜', '☀️', '🌝', '🌞', '⭐', '🌟', '🌠', '☁️', '⛅', '⛈️', '🌤️', '🌥️', '🌦️', '🌧️', '🌨️', '🌩️', '🌪️', '🌫️', '🌬️', '🌀', '🌈', '🌂', '☂️', '☔', '⛱️', '⚡', '❄️', '☃️', '⛄', '☄️', '🔥', '💧', '🌊'],
    'Objeler 💻': ['💻', '🖥️', '🖨️', '⌨️', '🖱️', '🖲️', '💽', '💾', '💿', '📀', '📱', '📲', '☎️', '📞', '📟', '📠', '🔋', '🔌', '💡', '🔦', '🕯️', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🔩', '⚙️', '🧱', '⛓️', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '⚱️', '🏺', '🔮', '📿', '🧿', '💈'],
    'Semboller ✨': ['✨', '💫', '💥', '💢', '💦', '💨', '🕳️', '💬', '👁️‍🗨️', '🗨️', '🗯️', '💭', '💤', '🔇', '🔈', '🔉', '🔊', '🔔', '🔕', '🎵', '🎶', '💹', '🏧', '🚮', '🚰', '♿', '🚹', '🚺', '🚻', '🚼', '🚾', '🛂', '🛃', '🛄', '🛅', '⚠️', '🚸', '⛔', '🚫', '🚳', '🚭', '🚯', '🚱', '🚷', '📵', '🔞', '☢️', '☣️', '⬆️', '↗️', '➡️', '↘️', '⬇️', '↙️', '⬅️', '↖️', '↕️', '↔️', '↩️', '↪️', '⤴️', '⤵️', '🔃', '🔄', '🔙', '🔚', '🔛', '🔜', '🔝', '✅', '☑️', '✔️', '❌', '❎', '➕', '➖', '➗', '✖️', '💲', '💱', '©️', '®️', '™️', '🔘', '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺', '🔻', '🔸', '🔹', '🔶', '🔷', '🔳', '🔲', '▪️', '▫️', '◾', '◽', '◼️', '◻️', '⬛', '⬜', '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '🟫']
  };

  // Hızlı erişim emojileri (en popüler)
  const quickEmojis = ['😀', '😂', '🤣', '😍', '🥰', '😘', '😎', '🤔', '😏', '😢', '😭', '😤', '🥳', '🎉', '❤️', '👍', '👎', '👏', '🙏', '💪', '🔥', '✨', '💯', '✅', '❌'];

  const fileInputRef = useRef(null);
  const roomFileInputRef = useRef(null);

  // Room emoji picker state
  const [showRoomEmojiPicker, setShowRoomEmojiPicker] = useState(false);
  const roomEmojiPickerRef = useRef(null);

  // Sesli arama state'leri
  const [callState, setCallState] = useState('idle'); // idle, calling, incoming, active
  const [incomingCall, setIncomingCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);
  const ringtoneRef = useRef(null);
  const dialingRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const selectedUserRef = useRef(null);
  const audioUnlocked = useRef(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');

  // Play sound helper - creates audio on demand within user gesture
  const playSound = useCallback((soundName, loop = false, volume = 0.5) => {
    const audio = new Audio(`/${soundName}.mp3`);
    audio.loop = loop;
    audio.volume = volume;
    audio.play().then(() => {
      console.log(`🔊 Playing ${soundName}`);
    }).catch(err => {
      console.log(`Sound error ${soundName}:`, err);
    });
    return audio;
  }, []);

  // Stop sound helper
  const stopSound = useCallback((audioRef) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
  }, []);

  // Keep refs in sync with state
  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    selectedRoomRef.current = selectedRoom;
  }, [selectedRoom]);

  useEffect(() => {
    conferenceActiveRef.current = conferenceActive;
  }, [conferenceActive]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Initialize socket connection
  useEffect(() => {
    if (!token) return;

    const newSocket = io(SOCKET_URL, {
      auth: { token }
    });

    newSocket.on('connect', () => {
      console.log('Chat connected');
    });

    newSocket.on('users:online', (userIds) => {
      setOnlineUsers(userIds);
    });

    newSocket.on('user:online', ({ userId }) => {
      setOnlineUsers(prev => [...new Set([...prev, userId])]);
    });

    newSocket.on('user:offline', ({ userId }) => {
      setOnlineUsers(prev => prev.filter(id => id !== userId));
    });

    newSocket.on('message:receive', (message) => {
      const currentSelectedUser = selectedUserRef.current;
      if (currentSelectedUser && message.senderId === currentSelectedUser.id) {
        setMessages(prev => [...prev, message]);
        // Mark as read immediately
        newSocket.emit('message:read', {
          senderId: message.senderId,
          messageIds: [message.id]
        });
      } else {
        // Show notification for unread message
        const senderName = message.sender?.fullName || 'Birisi';
        const messagePreview = message.content?.substring(0, 50) || 'Dosya gönderdi';

        // Add to pending notifications
        setPendingNotifications(prev => [...prev, {
          id: message.id,
          senderId: message.senderId,
          senderName,
          content: messagePreview,
          createdAt: message.createdAt
        }]);

        // Play notification sound
        try {
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.5;
          audio.play().catch(() => {});
        } catch (e) {}

        // Show browser notification
        if (Notification.permission === 'granted') {
          new Notification(`Yeni Mesaj - ${senderName}`, {
            body: messagePreview,
            icon: '/logo192.png',
            tag: `message-${message.id}`
          });
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              new Notification(`Yeni Mesaj - ${senderName}`, {
                body: messagePreview,
                icon: '/logo192.png',
                tag: `message-${message.id}`
              });
            }
          });
        }
      }
      // Update departments and recent
      fetchDepartments();
      fetchRecentConversations();
    });

    newSocket.on('message:sent', (message) => {
      setMessages(prev => [...prev, message]);
      fetchRecentConversations();
    });

    newSocket.on('message:delivered', ({ messageId }) => {
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, status: 'delivered' } : m
      ));
    });

    newSocket.on('message:read', ({ messageIds }) => {
      setMessages(prev => prev.map(m =>
        messageIds.includes(m.id) ? { ...m, status: 'read' } : m
      ));
    });

    newSocket.on('typing:start', ({ userId, fullName }) => {
      const currentSelectedUser = selectedUserRef.current;
      if (currentSelectedUser && userId === currentSelectedUser.id) {
        setTyping(fullName);
      }
    });

    newSocket.on('typing:stop', ({ userId }) => {
      const currentSelectedUser = selectedUserRef.current;
      if (currentSelectedUser && userId === currentSelectedUser.id) {
        setTyping(null);
      }
    });

    // =====================
    // ROOM MESSAGE EVENTS
    // =====================
    newSocket.on('room:message', (message) => {
      setRoomMessages(prev => [...prev, message]);
      fetchChatRooms(); // Update room list for last message

      // Show notification if message is from someone else
      if (message.senderId !== currentUser.id) {
        const senderName = message.sender?.fullName || 'Birisi';
        const messagePreview = message.content?.substring(0, 50) || 'Dosya gönderdi';

        // Play notification sound
        try {
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.5;
          audio.play().catch(() => {});
        } catch (e) {}

        // Show browser notification
        if (Notification.permission === 'granted') {
          new Notification(`Grup Mesajı - ${senderName}`, {
            body: messagePreview,
            icon: '/logo192.png',
            tag: `room-message-${message.id}`
          });
        }
      }
    });

    newSocket.on('room:typing:start', ({ userId, fullName }) => {
      setTyping(fullName);
    });

    newSocket.on('room:typing:stop', () => {
      setTyping(null);
    });

    // Room file message received
    newSocket.on('room:file', ({ roomId, message }) => {
      const currentRoom = selectedRoomRef.current;
      if (currentRoom && currentRoom.id === roomId) {
        setRoomMessages(prev => {
          // Prevent duplicate messages
          if (prev.some(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }
      fetchChatRooms(); // Update room list
    });

    // =====================
    // BROADCAST EVENTS
    // =====================
    newSocket.on('broadcast:receive', (broadcast) => {
      setBroadcasts(prev => [broadcast, ...prev]);
      const senderName = broadcast.sender?.fullName || 'Sistem';
      const messagePreview = broadcast.content?.substring(0, 50) || 'Yeni duyuru';

      // Play notification sound
      try {
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {}

      // Show browser notification
      if (Notification.permission === 'granted') {
        new Notification(`Duyuru - ${senderName}`, {
          body: messagePreview,
          icon: '/logo192.png',
          tag: `broadcast-${broadcast.id}`
        });
      }
    });

    // =====================
    // CONFERENCE CALL EVENTS
    // =====================
    newSocket.on('conference:started', ({ roomId, initiatorId, initiatorName }) => {
      // Don't show notification if we are the initiator
      if (initiatorId === currentUser.id) return;

      // Check if user is in this room
      const currentSelectedRoom = selectedRoomRef.current;
      if (!currentSelectedRoom || currentSelectedRoom.id !== parseInt(roomId)) return;

      setIncomingConference({ roomId, initiatorId, initiatorName });
      // Play ringtone
      const ring = new Audio('/ringtone.mp3');
      ring.loop = true;
      ring.volume = 0.7;
      ring.play().catch(() => {});
      ringtoneRef.current = ring;
    });

    newSocket.on('conference:user-joined', async ({ odaId, userId, fullName }) => {
      // odaId is sent by backend, use it as the user identifier
      const joinedUserId = odaId || userId;
      console.log(`📞 ${fullName} konferansa katıldı (userId: ${joinedUserId})`);

      // Avoid duplicates
      setConferenceParticipants(prev => {
        if (prev.find(p => p.odaId === joinedUserId)) return prev;
        return [...prev, { odaId: joinedUserId, fullName }];
      });

      // If we're in a conference, send WebRTC offer to the new participant
      if (conferenceActiveRef.current && joinedUserId !== currentUser?.id) {
        console.log(`📞 Sending offer to ${fullName} (${joinedUserId})`);
        try {
          const pc = new RTCPeerConnection(ICE_SERVERS);
          conferencePeersRef.current[joinedUserId] = pc;

          // Add local stream tracks if available
          if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => {
              pc.addTrack(track, localStreamRef.current);
            });
          }

          // Handle remote stream
          pc.ontrack = (event) => {
            console.log(`📞 Received audio track from ${fullName}`);
            const audio = new Audio();
            audio.srcObject = event.streams[0];
            audio.autoplay = true;
            audio.muted = roomSpeakerMutedRef.current;
            audio.volume = roomSpeakerMutedRef.current ? 0 : 1;
            audio.play().catch(e => console.error('Audio play error:', e));
            conferenceAudiosRef.current[joinedUserId] = audio;
          };

          // ICE candidates
          pc.onicecandidate = (event) => {
            if (event.candidate) {
              newSocket.emit('conference:ice-candidate', {
                roomId: selectedRoomRef.current?.id,
                targetUserId: joinedUserId,
                candidate: event.candidate
              });
            }
          };

          // Create and send offer
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          newSocket.emit('conference:offer', {
            roomId: selectedRoomRef.current?.id,
            targetUserId: joinedUserId,
            offer
          });
        } catch (error) {
          console.error('Error creating offer for new participant:', error);
        }
      }
    });

    newSocket.on('conference:user-left', ({ userId, fullName }) => {
      console.log(`📞 ${fullName} konferanstan ayrıldı`);
      setConferenceParticipants(prev => prev.filter(p => p.odaId !== userId));
      // Close peer connection for this user
      if (conferencePeersRef.current[userId]) {
        conferencePeersRef.current[userId].close();
        delete conferencePeersRef.current[userId];
      }
      // Stop and remove audio element for this user
      if (conferenceAudiosRef.current[userId]) {
        conferenceAudiosRef.current[userId].pause();
        conferenceAudiosRef.current[userId].srcObject = null;
        delete conferenceAudiosRef.current[userId];
      }
    });

    // Handle receiving list of existing participants when joining a conference
    // This enables mesh network - new joiner connects to ALL existing participants
    newSocket.on('conference:participants', async ({ roomId, participants }) => {
      console.log(`📞 Received existing participants list:`, participants, 'conferenceActive:', conferenceActiveRef.current);

      // Activate conference if not already active
      if (!conferenceActiveRef.current) {
        console.log('📞 Activating conference from participants list');
        conferenceActiveRef.current = true;
        setConferenceActive(true);
        setConferenceRoomId(roomId);
      }

      // Can proceed even without localStream - will be receive-only
      if (!localStreamRef.current) {
        console.log('📞 No local stream - will be receive-only');
      }

      // Create peer connections with ALL existing participants
      for (const participant of participants) {
        const targetUserId = participant.userId;
        if (targetUserId === currentUser?.id) continue; // Skip self
        if (conferencePeersRef.current[targetUserId]) {
          console.log(`📞 Already have connection to ${participant.fullName}`);
          continue; // Already have connection
        }

        console.log(`📞 Creating mesh connection to ${participant.fullName} (${targetUserId})`);
        try {
          const pc = new RTCPeerConnection(ICE_SERVERS);
          conferencePeersRef.current[targetUserId] = pc;

          // Add local stream tracks if available
          if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => {
              pc.addTrack(track, localStreamRef.current);
            });
          }

          // Handle remote stream
          pc.ontrack = (event) => {
            console.log(`📞 Received audio track from ${participant.fullName}`);
            const audio = new Audio();
            audio.srcObject = event.streams[0];
            audio.autoplay = true;
            audio.muted = roomSpeakerMutedRef.current;
            audio.volume = roomSpeakerMutedRef.current ? 0 : 1;
            audio.play().catch(e => console.error('Audio play error:', e));
            conferenceAudiosRef.current[targetUserId] = audio;
          };

          // ICE candidates
          pc.onicecandidate = (event) => {
            if (event.candidate) {
              newSocket.emit('conference:ice-candidate', {
                roomId: roomId,
                targetUserId: targetUserId,
                candidate: event.candidate
              });
            }
          };

          pc.onconnectionstatechange = () => {
            console.log(`📞 Mesh connection to ${participant.fullName}: ${pc.connectionState}`);
          };

          // Create and send offer
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          newSocket.emit('conference:offer', {
            roomId: roomId,
            targetUserId: targetUserId,
            offer
          });
          console.log(`📞 Sent mesh offer to ${participant.fullName}`);

          // Add to participants list
          setConferenceParticipants(prev => {
            if (prev.find(p => p.odaId === targetUserId)) return prev;
            return [...prev, { odaId: targetUserId, fullName: participant.fullName }];
          });
        } catch (error) {
          console.error(`Error creating mesh connection to ${participant.fullName}:`, error);
        }
      }
    });

    newSocket.on('conference:offer', async ({ roomId, senderId, offer }) => {
      console.log('📞 Received offer from user:', senderId, 'conferenceActive:', conferenceActiveRef.current);

      // If we receive an offer but conference isn't active, activate it now
      // This handles the case where the other user joined first
      if (!conferenceActiveRef.current) {
        console.log('📞 Conference not active, activating now to accept offer');
        conferenceActiveRef.current = true;
        setConferenceActive(true);
        setConferenceRoomId(roomId);
      }

      try {
        // Check if we already have a connection with this user
        let pc = conferencePeersRef.current[senderId];
        if (pc) {
          // If we have a pending offer (have-local-offer state), use "polite peer" pattern
          // The peer with higher ID yields (is polite)
          if (pc.signalingState === 'have-local-offer') {
            const myId = currentUser?.id || 0;
            if (myId > senderId) {
              // I'm polite - close my offer and accept theirs
              console.log('📞 Collision detected - I am polite, accepting their offer');
              pc.close();
              delete conferencePeersRef.current[senderId];
            } else {
              // I'm impolite - ignore their offer, they should accept mine
              console.log('📞 Collision detected - I am impolite, ignoring their offer');
              return;
            }
          } else if (pc.signalingState === 'stable') {
            // Connection already established, ignore duplicate offer
            console.log('📞 Connection already stable, ignoring offer');
            return;
          } else {
            // Other state - close and recreate
            console.log('📞 Closing existing connection in state:', pc.signalingState);
            pc.close();
            delete conferencePeersRef.current[senderId];
          }
        }

        // Create peer connection for this user
        pc = new RTCPeerConnection(ICE_SERVERS);
        conferencePeersRef.current[senderId] = pc;

        // Add local stream if available
        if (localStreamRef.current) {
          console.log('📞 Adding local stream tracks to peer connection');
          localStreamRef.current.getTracks().forEach(track => {
            pc.addTrack(track, localStreamRef.current);
          });
        } else {
          console.log('📞 No local stream, will only receive audio');
        }

        pc.ontrack = (event) => {
          console.log('📞 Received remote audio track from:', senderId);
          // Play remote audio
          const audio = new Audio();
          audio.srcObject = event.streams[0];
          audio.autoplay = true;
          audio.muted = roomSpeakerMutedRef.current;
          audio.volume = roomSpeakerMutedRef.current ? 0 : 1;
          audio.play().catch(e => console.error('Audio play error:', e));
          conferenceAudiosRef.current[senderId] = audio;
        };

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            newSocket.emit('conference:ice-candidate', {
              roomId,
              targetUserId: senderId,
              candidate: event.candidate
            });
          }
        };

        pc.onconnectionstatechange = () => {
          console.log('📞 Peer connection state:', pc.connectionState);
        };

        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        newSocket.emit('conference:answer', { roomId, targetUserId: senderId, answer });
        console.log('📞 Sent answer to user:', senderId);
      } catch (error) {
        console.error('📞 Error handling offer:', error);
      }
    });

    newSocket.on('conference:answer', async ({ roomId, senderId, answer }) => {
      const pc = conferencePeersRef.current[senderId];
      if (pc) {
        // Only set remote description if we're in the right state (have-local-offer)
        if (pc.signalingState === 'have-local-offer') {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(answer));
            console.log('📞 Set remote description for user:', senderId);
          } catch (error) {
            console.error('📞 Error setting remote description:', error);
          }
        } else {
          console.log('📞 Ignoring answer - wrong state:', pc.signalingState);
        }
      }
    });

    newSocket.on('conference:ice-candidate', async ({ roomId, senderId, candidate }) => {
      const pc = conferencePeersRef.current[senderId];
      if (pc && candidate) {
        try {
          // Only add ICE candidate if remote description is set
          if (pc.remoteDescription && pc.remoteDescription.type) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } else {
            // Queue the candidate for later - store in a buffer
            if (!pc.pendingCandidates) {
              pc.pendingCandidates = [];
            }
            pc.pendingCandidates.push(candidate);
          }
        } catch (e) {
          // Ignore errors - ICE candidates can arrive out of order
          console.log('ICE candidate skipped:', e.message);
        }
      }
    });

    // =====================
    // VOICE CALL EVENTS
    // =====================

    // Incoming call
    newSocket.on('call:incoming', ({ callerId, callerName }) => {
      console.log('📞 Incoming call from:', callerName);
      setIncomingCall({ callerId, callerName });
      setCallState('incoming');
      // Play ringtone - create new audio each time
      const ring = new Audio('/ringtone.mp3');
      ring.loop = true;
      ring.volume = 0.7;
      ring.play().catch(err => console.log('Ringtone error:', err));
      ringtoneRef.current = ring;
      console.log('🔔 Ringtone started');
    });

    // Call ringing (caller side) - dialing tone already started in initiateCall
    newSocket.on('call:ringing', () => {
      console.log('📞 Call ringing - waiting for answer');
      // Dialing sound is already playing from initiateCall (user gesture context)
    });

    // Call unavailable
    newSocket.on('call:unavailable', ({ reason }) => {
      alert(reason || 'Kullanıcı müsait değil');
      setCallState('idle');
    });

    // Call accepted - Now create and send WebRTC offer
    newSocket.on('call:accepted', async ({ acceptedBy, acceptedByName }) => {
      console.log('✅ Call accepted by:', acceptedByName);

      // Stop dialing tone
      if (dialingRef.current) {
        dialingRef.current.pause();
        dialingRef.current = null;
      }

      setCallState('active');

      // Start call timer
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);

      // Create and send WebRTC offer
      if (peerConnectionRef.current) {
        try {
          const offer = await peerConnectionRef.current.createOffer();
          await peerConnectionRef.current.setLocalDescription(offer);
          newSocket.emit('webrtc:offer', { receiverId: acceptedBy, offer });
          console.log('📤 WebRTC offer sent');
        } catch (e) {
          console.error('Error creating offer:', e);
        }
      }
    });

    // Call rejected
    newSocket.on('call:rejected', ({ reason }) => {
      console.log('❌ Call rejected:', reason);
      // Stop dialing tone and play hangup sound
      if (dialingRef.current) {
        dialingRef.current.pause();
        dialingRef.current = null;
      }
      // Play hangup sound
      try {
        const hangup = new Audio('/hangup.mp3');
        hangup.volume = 0.6;
        hangup.play().catch(() => {});
      } catch (e) {}
      setCallState('idle');
      setCallDuration(0);
      alert(reason || 'Arama reddedildi');
    });

    // Call ended
    newSocket.on('call:ended', () => {
      console.log('📴 Call ended');
      // Play hangup sound
      try {
        const hangup = new Audio('/hangup.mp3');
        hangup.volume = 0.6;
        hangup.play().catch(() => {});
      } catch (e) {}
      endCallCleanup();
    });

    // WebRTC: Receive offer
    newSocket.on('webrtc:offer', async ({ senderId, offer }) => {
      if (peerConnectionRef.current) {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await peerConnectionRef.current.createAnswer();
        await peerConnectionRef.current.setLocalDescription(answer);
        newSocket.emit('webrtc:answer', { receiverId: senderId, answer });
      }
    });

    // WebRTC: Receive answer
    newSocket.on('webrtc:answer', async ({ answer }) => {
      if (peerConnectionRef.current) {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
      }
    });

    // WebRTC: Receive ICE candidate
    newSocket.on('webrtc:ice-candidate', async ({ candidate }) => {
      if (peerConnectionRef.current && candidate) {
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.error('Error adding ICE candidate:', e);
        }
      }
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, [token]);

  // Fetch departments and users
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await api.get('/chat/users');
      setDepartments(response.data);

      // Auto-expand IT department
      const itDept = response.data.find(d => d.isIT);
      if (itDept) {
        setExpandedDepts(prev => ({ ...prev, [itDept.name]: true }));
      }
    } catch (error) {
      console.error('Error fetching chat users:', error);
    }
  }, []);

  // Fetch recent conversations
  const fetchRecentConversations = useCallback(async () => {
    try {
      const response = await api.get('/chat/recent');
      setRecentConversations(response.data);
    } catch (error) {
      console.error('Error fetching recent conversations:', error);
    }
  }, []);

  // Fetch chat rooms
  const fetchChatRooms = useCallback(async () => {
    try {
      const response = await api.get('/chat-rooms');
      setChatRooms(response.data);
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
    }
  }, []);

  // Fetch broadcasts
  const fetchBroadcasts = useCallback(async () => {
    try {
      const response = await api.get('/broadcast');
      setBroadcasts(response.data);
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    }
  }, []);

  // Fetch all users for room creation
  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setAllUsers(response.data.filter(u => u.id !== currentUser.id));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [currentUser.id]);

  // Fetch room messages
  const fetchRoomMessages = useCallback(async (roomId) => {
    try {
      const response = await api.get(`/chat-rooms/${roomId}/messages`);
      setRoomMessages(response.data);
    } catch (error) {
      console.error('Error fetching room messages:', error);
    }
  }, []);

  // Create chat room
  const createChatRoom = async () => {
    if (!newRoomName.trim()) return;

    try {
      await api.post('/chat-rooms', {
        name: newRoomName.trim(),
        description: newRoomDescription.trim(),
        memberIds: selectedRoomMembers
      });
      setShowCreateRoomModal(false);
      setNewRoomName('');
      setNewRoomDescription('');
      setSelectedRoomMembers([]);
      fetchChatRooms();
    } catch (error) {
      console.error('Error creating room:', error);
      alert('Oda oluşturulurken hata oluştu');
    }
  };

  // Send room message
  const sendRoomMessage = () => {
    if (!newMessage.trim() || !socket || !selectedRoom) return;

    socket.emit('room:message', {
      roomId: selectedRoom.id,
      content: newMessage.trim(),
      messageType: 'text'
    });

    setNewMessage('');
  };

  // Send broadcast
  const sendBroadcast = async () => {
    if (!broadcastContent.trim()) return;

    try {
      await api.post('/broadcast', {
        targetType: broadcastTarget,
        targetDepartmentId: broadcastTarget === 'department' ? parseInt(broadcastDeptId) : null,
        content: broadcastContent.trim(),
        priority: broadcastPriority
      });
      setShowBroadcastModal(false);
      setBroadcastContent('');
      setBroadcastTarget('all');
      setBroadcastDeptId('');
      setBroadcastPriority('normal');
      fetchBroadcasts();
    } catch (error) {
      console.error('Error sending broadcast:', error);
      alert(error.response?.data?.error || 'Duyuru gönderilirken hata oluştu');
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchRecentConversations();
    fetchChatRooms();
    fetchBroadcasts();
    fetchAllUsers();
  }, [fetchDepartments, fetchRecentConversations, fetchChatRooms, fetchBroadcasts, fetchAllUsers]);

  // Fetch messages when user selected
  useEffect(() => {
    if (!selectedUser) return;

    const fetchMessages = async () => {
      try {
        const response = await api.get(`/chat/conversation/${selectedUser.id}`);
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    fetchMessages();
  }, [selectedUser]);

  // Scroll to bottom on new messages (individual chat)
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages]);

  // Scroll to bottom on new room messages
  useEffect(() => {
    if (roomMessages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [roomMessages]);

  const toggleDepartment = (deptName) => {
    setExpandedDepts(prev => ({ ...prev, [deptName]: !prev[deptName] }));
  };

  const selectUser = (user) => {
    setSelectedUser(user);
    setMessages([]);
    // Update unread count in departments
    setDepartments(prev => prev.map(dept => ({
      ...dept,
      users: dept.users.map(u =>
        u.id === user.id ? { ...u, unreadCount: 0 } : u
      )
    })));
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() || !socket || !selectedUser) return;

    socket.emit('message:send', {
      receiverId: selectedUser.id,
      content: newMessage.trim(),
      messageType: 'text'
    });

    setNewMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTyping = () => {
    if (!socket || !selectedUser) return;

    socket.emit('typing:start', { receiverId: selectedUser.id });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('typing:stop', { receiverId: selectedUser.id });
    }, 2000);
  };

  // Emoji ekleme fonksiyonu
  const addEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
  };

  // Emoji picker dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (roomEmojiPickerRef.current && !roomEmojiPickerRef.current.contains(event.target)) {
        setShowRoomEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // =====================
  // CONFERENCE CALL FUNCTIONS
  // =====================

  // Start conference call in a room
  const startConference = async () => {
    if (!selectedRoom || !socket) return;

    try {
      // Notify other room members first (so they get notification immediately)
      socket.emit('conference:start', { roomId: selectedRoom.id });

      // Join conference
      socket.emit('conference:join', { roomId: selectedRoom.id });

      setConferenceActive(true);
      setConferenceRoomId(selectedRoom.id);
      setConferenceParticipants([{ odaId: currentUser.id, fullName: currentUser.fullName + ' (Sen)' }]);

      console.log('📞 Conference started in room:', selectedRoom.name);

      // Try to get local audio stream (may fail if no microphone)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        localStreamRef.current = stream;
      } catch (micError) {
        console.warn('Mikrofon erişimi yok, sadece bildirim gönderildi:', micError);
      }
    } catch (error) {
      console.error('Error starting conference:', error);
      alert('Konferans başlatılamadı');
    }
  };

  // Join existing conference
  const joinConference = async () => {
    if (!incomingConference || !socket) return;

    // Stop ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }

    const roomId = incomingConference.roomId;

    try {
      // Try to get local audio stream (may fail if no microphone)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        localStreamRef.current = stream;
        console.log('📞 Mikrofon erişimi başarılı');
      } catch (micError) {
        console.warn('📞 Mikrofon erişimi yok, dinleme modunda katılıyor:', micError);
      }

      // Join conference
      socket.emit('conference:join', { roomId });

      setConferenceActive(true);
      setConferenceRoomId(roomId);
      setConferenceParticipants([{ odaId: currentUser.id, fullName: currentUser.fullName + ' (Sen)' }]);
      setIncomingConference(null);

      // The server will notify us about other participants via conference:user-joined
      // and they will send us offers
      console.log('📞 Joined conference in room:', roomId);
    } catch (error) {
      console.error('Error joining conference:', error);
      alert('Konferansa katılınamadı');
      declineConference();
    }
  };

  // Decline incoming conference
  const declineConference = () => {
    // Stop ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }
    setIncomingConference(null);
  };

  // Leave conference
  const leaveConference = () => {
    if (!socket) return;

    // Notify server
    if (conferenceRoomId) {
      socket.emit('conference:leave', { roomId: conferenceRoomId });
    }

    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Close all peer connections
    Object.keys(conferencePeersRef.current).forEach(odaId => {
      conferencePeersRef.current[odaId].close();
    });
    conferencePeersRef.current = {};

    // Stop all remote audios
    Object.keys(conferenceAudiosRef.current).forEach(odaId => {
      conferenceAudiosRef.current[odaId].pause();
      conferenceAudiosRef.current[odaId].srcObject = null;
    });
    conferenceAudiosRef.current = {};

    setConferenceActive(false);
    setConferenceRoomId(null);
    setConferenceParticipants([]);

    console.log('📞 Left conference');
  };

  // =====================
  // ROOM AUDIO FUNCTIONS (Always-on voice for rooms)
  // =====================

  // Start room audio session when entering a room
  const startRoomAudio = async (roomId) => {
    if (roomAudioActiveRef.current) return; // Already active

    console.log('🔊 Starting room audio session for room:', roomId);

    try {
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      roomStreamRef.current = stream;
      localStreamRef.current = stream; // Set this BEFORE emitting join

      // Mute microphone by default
      stream.getAudioTracks().forEach(track => {
        track.enabled = false;
      });

      // Set refs BEFORE emitting join so conference:participants handler can use them
      setRoomAudioActive(true);
      roomAudioActiveRef.current = true;
      setRoomMicMuted(true);
      setRoomSpeakerMuted(false); // Speaker unmuted so user can hear others
      roomSpeakerMutedRef.current = false;
      setConferenceActive(true);
      conferenceActiveRef.current = true;
      setConferenceRoomId(roomId);

      // Join conference room AFTER setting all refs
      if (socket) {
        socket.emit('conference:join', { roomId });
      }

      console.log('🔊 Room audio session started (mic muted, speaker on)');
    } catch (error) {
      console.warn('🔊 Could not get microphone access:', error);
      // Set refs BEFORE emitting join
      setRoomAudioActive(true);
      roomAudioActiveRef.current = true;
      setRoomMicMuted(true);
      setRoomSpeakerMuted(false); // Speaker unmuted so user can hear others
      roomSpeakerMutedRef.current = false;
      setConferenceActive(true);
      conferenceActiveRef.current = true;
      setConferenceRoomId(roomId);
      // Still join without microphone AFTER setting refs
      if (socket) {
        socket.emit('conference:join', { roomId });
      }
    }
  };

  // Stop room audio session when leaving a room
  const stopRoomAudio = () => {
    console.log('🔊 Stopping room audio session');

    // Leave conference
    if (socket && conferenceRoomId) {
      socket.emit('conference:leave', { roomId: conferenceRoomId });
    }

    // Stop microphone stream
    if (roomStreamRef.current) {
      roomStreamRef.current.getTracks().forEach(track => track.stop());
      roomStreamRef.current = null;
    }

    // Close all peer connections
    Object.keys(conferencePeersRef.current).forEach(odaId => {
      conferencePeersRef.current[odaId].close();
    });
    conferencePeersRef.current = {};

    // Stop all remote audios
    Object.keys(conferenceAudiosRef.current).forEach(odaId => {
      conferenceAudiosRef.current[odaId].pause();
      conferenceAudiosRef.current[odaId].srcObject = null;
    });
    conferenceAudiosRef.current = {};

    setRoomAudioActive(false);
    roomAudioActiveRef.current = false;
    setConferenceActive(false);
    conferenceActiveRef.current = false;
    setConferenceRoomId(null);
    setConferenceParticipants([]);
    localStreamRef.current = null;
  };

  // Toggle room microphone
  const toggleRoomMic = async () => {
    if (!roomAudioActive) return;

    if (roomMicMuted) {
      // Unmute - need to enable track or get new stream
      if (roomStreamRef.current) {
        roomStreamRef.current.getAudioTracks().forEach(track => {
          track.enabled = true;
        });
      } else {
        // Try to get microphone access
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          roomStreamRef.current = stream;
          localStreamRef.current = stream;

          // Add tracks to existing peer connections
          Object.keys(conferencePeersRef.current).forEach(odaId => {
            const pc = conferencePeersRef.current[odaId];
            stream.getAudioTracks().forEach(track => {
              pc.addTrack(track, stream);
            });
          });
        } catch (error) {
          console.error('Could not get microphone access:', error);
          return;
        }
      }
      setRoomMicMuted(false);
      console.log('🎤 Room mic unmuted');
    } else {
      // Mute
      if (roomStreamRef.current) {
        roomStreamRef.current.getAudioTracks().forEach(track => {
          track.enabled = false;
        });
      }
      setRoomMicMuted(true);
      console.log('🎤 Room mic muted');
    }
  };

  // Toggle room speaker
  const toggleRoomSpeaker = () => {
    if (!roomAudioActive) return;

    const newMuted = !roomSpeakerMuted;
    roomSpeakerMutedRef.current = newMuted; // Update ref for async handlers

    // Mute/unmute all remote audio elements
    Object.keys(conferenceAudiosRef.current).forEach(odaId => {
      const audio = conferenceAudiosRef.current[odaId];
      if (audio) {
        audio.muted = newMuted;
        audio.volume = newMuted ? 0 : 1;
      }
    });

    setRoomSpeakerMuted(newMuted);
    console.log(`🔊 Room speaker ${newMuted ? 'muted' : 'unmuted'}`);
  };

  // =====================
  // ROOM MANAGEMENT FUNCTIONS
  // =====================

  // Open room settings modal
  const openRoomSettings = async () => {
    if (!selectedRoom) return;
    try {
      const response = await api.get(`/chat-rooms/${selectedRoom.id}`);
      setRoomDetails(response.data);
      setAddMemberIds([]);
      setShowRoomSettingsModal(true);
    } catch (error) {
      console.error('Error fetching room details:', error);
      alert('Oda bilgileri alınamadı');
    }
  };

  // Add members to room
  const addMembersToRoom = async () => {
    if (!roomDetails || addMemberIds.length === 0) return;
    try {
      await api.post(`/chat-rooms/${roomDetails.id}/members`, { userIds: addMemberIds });
      // Refresh room details
      const response = await api.get(`/chat-rooms/${roomDetails.id}`);
      setRoomDetails(response.data);
      setAddMemberIds([]);
      fetchChatRooms();
      alert('Üyeler başarıyla eklendi');
    } catch (error) {
      console.error('Error adding members:', error);
      alert(error.response?.data?.error || 'Üye eklenirken hata oluştu');
    }
  };

  // Remove member from room
  const removeMemberFromRoom = async (memberId) => {
    if (!roomDetails) return;
    if (!window.confirm('Bu üyeyi odadan çıkarmak istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/chat-rooms/${roomDetails.id}/members/${memberId}`);
      // Refresh room details
      const response = await api.get(`/chat-rooms/${roomDetails.id}`);
      setRoomDetails(response.data);
      fetchChatRooms();
    } catch (error) {
      console.error('Error removing member:', error);
      alert(error.response?.data?.error || 'Üye çıkarılırken hata oluştu');
    }
  };

  // Delete room (admin only)
  const deleteRoom = async () => {
    if (!roomDetails) return;
    if (!window.confirm('Bu odayı silmek istediğinize emin misiniz? Mesajlar korunacak ancak oda artık görünmeyecek.')) return;
    try {
      // Stop room audio session first
      if (roomAudioActive) {
        stopRoomAudio();
      }
      await api.delete(`/chat-rooms/${roomDetails.id}`);
      setShowRoomSettingsModal(false);
      setRoomDetails(null);
      setSelectedRoom(null);
      setRoomMessages([]);
      fetchChatRooms();
      alert('Oda başarıyla silindi');
    } catch (error) {
      console.error('Error deleting room:', error);
      alert(error.response?.data?.error || 'Oda silinirken hata oluştu');
    }
  };

  // Delete archived message (admin only)
  const deleteArchivedMessage = async (messageId) => {
    if (!window.confirm('Bu arşivlenmiş mesajı kalıcı olarak silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/chat/archive/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      alert('Mesaj başarıyla silindi');
    } catch (error) {
      console.error('Error deleting archived message:', error);
      alert(error.response?.data?.error || 'Mesaj silinirken hata oluştu');
    }
  };

  // =====================
  // VOICE CALL FUNCTIONS
  // =====================

  // Start a call
  const initiateCall = async () => {
    if (!selectedUser || !socket) return;

    try {
      // First initiate call via socket (so receiver gets notification immediately)
      socket.emit('call:initiate', { receiverId: selectedUser.id });
      console.log('📞 Call initiated to:', selectedUser.fullName);

      // Start dialing tone immediately
      setCallState('calling');
      dialingRef.current = playSound('dialing', true, 0.5);

      // Try to get local audio stream (may fail if no microphone)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        localStreamRef.current = stream;

        // Create peer connection
        const pc = new RTCPeerConnection(ICE_SERVERS);
        peerConnectionRef.current = pc;

        // Add local stream tracks to peer connection
        stream.getTracks().forEach(track => {
          pc.addTrack(track, stream);
        });

        // Handle incoming remote stream
        pc.ontrack = (event) => {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = event.streams[0];
          }
        };

        // Handle ICE candidates
        pc.onicecandidate = (event) => {
          if (event.candidate) {
            socket.emit('webrtc:ice-candidate', {
              receiverId: selectedUser.id,
              candidate: event.candidate
            });
          }
        };
      } catch (micError) {
        console.warn('Mikrofon erişimi yok, sadece bildirim gönderildi:', micError);
      }

    } catch (error) {
      console.error('Error initiating call:', error);
      alert('Arama başlatılamadı');
      setCallState('idle');
    }
  };

  // Accept incoming call
  const acceptCall = async () => {
    if (!incomingCall || !socket) return;

    // Stop ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }

    try {
      // Get local audio stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;

      // Create peer connection
      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerConnectionRef.current = pc;

      // Add local stream tracks
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // Handle incoming remote stream
      pc.ontrack = (event) => {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.streams[0];
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit('webrtc:ice-candidate', {
            receiverId: incomingCall.callerId,
            candidate: event.candidate
          });
        }
      };

      // Accept call
      socket.emit('call:accept', { callerId: incomingCall.callerId });
      setCallState('active');

      // Start call timer
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);

      // Select the caller as current user
      setSelectedUser({ id: incomingCall.callerId, fullName: incomingCall.callerName });
      setIncomingCall(null);

    } catch (error) {
      console.error('Error accepting call:', error);
      alert('Mikrofon erişimi reddedildi');
      rejectCall();
    }
  };

  // Reject incoming call
  const rejectCall = () => {
    if (!incomingCall || !socket) return;

    // Stop ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }

    socket.emit('call:reject', { callerId: incomingCall.callerId, reason: 'Meşgul' });
    setIncomingCall(null);
    setCallState('idle');
  };

  // End call
  const endCall = () => {
    if (!socket) return;

    const peerId = selectedUser?.id || incomingCall?.callerId;
    if (peerId) {
      socket.emit('call:end', { peerId });
    }

    endCallCleanup();
  };

  // Cleanup after call ends
  const endCallCleanup = () => {
    // Stop ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }

    // Stop dialing tone
    if (dialingRef.current) {
      dialingRef.current.pause();
      dialingRef.current = null;
    }

    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Clear timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    setCallState('idle');
    setCallDuration(0);
    setIncomingCall(null);
    setIsMuted(false);
  };

  // Toggle mute
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  // Toggle speaker (for remote audio)
  const toggleSpeaker = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = isSpeakerOn;
      setIsSpeakerOn(!isSpeakerOn);
    }
  };

  // Format call duration
  const formatCallDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedUser) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('receiverId', selectedUser.id);

    try {
      const response = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Notify via socket
      socket.emit('message:file', {
        messageId: response.data.id,
        receiverId: selectedUser.id
      });

      setMessages(prev => [...prev, response.data]);
    } catch (error) {
      console.error('File upload error:', error);
    }

    e.target.value = '';
  };

  // Room file upload handler
  const handleRoomFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedRoom) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/rooms/${selectedRoom.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Add message to local state
      setRoomMessages(prev => [...prev, response.data]);

      // Notify room members via socket
      socket.emit('room:file', {
        roomId: selectedRoom.id,
        message: response.data
      });
    } catch (error) {
      console.error('Room file upload error:', error);
      alert('Dosya yüklenirken hata oluştu');
    }

    e.target.value = '';
  };

  // Add emoji to room message
  const addRoomEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowRoomEmojiPicker(false);
  };

  const toggleArchive = async (messageId, e) => {
    e.stopPropagation();
    try {
      const response = await api.put(`/chat/archive/${messageId}`);
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, isArchived: response.data.isArchived } : m
      ));
    } catch (error) {
      console.error('Archive toggle error:', error);
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Bugün';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Dün';
    }
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  const renderStatus = (message) => {
    if (message.senderId !== currentUser.id) return null;

    if (message.status === 'read') {
      return <CheckIconSolid className="h-4 w-4 text-blue-500 inline ml-1" title="Okundu" />;
    } else if (message.status === 'delivered') {
      return (
        <span className="inline-flex ml-1" title="İletildi">
          <CheckIcon className="h-4 w-4 text-gray-400" />
          <CheckIcon className="h-4 w-4 text-gray-400 -ml-2" />
        </span>
      );
    } else {
      return <CheckIcon className="h-4 w-4 text-gray-400 inline ml-1" title="Gönderildi" />;
    }
  };

  const isUserOnline = (userId) => onlineUsers.includes(userId);

  // Filter users by search term
  const filteredDepartments = departments.map(dept => ({
    ...dept,
    users: dept.users.filter(user =>
      user.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(dept => dept.users.length > 0);

  // Get archived messages
  const archivedMessages = messages.filter(m => m.isArchived);

  if (!token) return null;

  // Check if we're on mobile and have a selected chat
  const showMobileChat = selectedUser || selectedRoom;

  return (
    <div className="h-[calc(100vh-8rem)] flex bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
      {/* Left Panel - Users/Recent */}
      <div className={`${showMobileChat ? 'hidden md:flex' : 'flex'} w-full md:w-80 border-r border-gray-200 dark:border-gray-700 flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
              Mesajlar
            </h2>
            {pendingNotifications.length > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1 animate-pulse">
                {pendingNotifications.length} yeni
              </span>
            )}
          </div>
          {/* Search */}
          <div className="mt-3 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Kullanıcı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Pending Notifications Panel */}
        {pendingNotifications.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800 p-2">
            <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-2">
              Okunmamış Mesajlar ({pendingNotifications.length})
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {pendingNotifications.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => {
                    setSelectedUser({ id: notif.senderId, fullName: notif.senderName });
                    setPendingNotifications(prev => prev.filter(n => n.senderId !== notif.senderId));
                  }}
                  className="w-full flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors text-left"
                >
                  <div className="h-8 w-8 rounded-full bg-red-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {notif.senderName.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{notif.senderName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{notif.content}</p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {formatTime(notif.createdAt)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 flex-wrap">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 py-2 text-xs font-medium ${
              activeTab === 'users'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Birimler
          </button>
          <button
            onClick={() => setActiveTab('recent')}
            className={`flex-1 py-2 text-xs font-medium ${
              activeTab === 'recent'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Geçmiş
          </button>
          <button
            onClick={() => setActiveTab('rooms')}
            className={`flex-1 py-2 text-xs font-medium ${
              activeTab === 'rooms'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <UserGroupIcon className="h-4 w-4 inline mr-1" />
            Odalar
          </button>
          <button
            onClick={() => setActiveTab('broadcast')}
            className={`flex-1 py-2 text-xs font-medium ${
              activeTab === 'broadcast'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <MegaphoneIcon className="h-4 w-4 inline mr-1" />
            Duyuru
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`flex-1 py-2 text-xs font-medium ${
              activeTab === 'archived'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Arşiv
          </button>
        </div>

        {/* User/Recent List */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'users' && (
            <>
              {filteredDepartments.map((dept) => (
                <div key={dept.name}>
                  <button
                    onClick={() => toggleDepartment(dept.name)}
                    className={`w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 ${
                      dept.isIT ? 'bg-red-50 dark:bg-red-900/20' : ''
                    }`}
                  >
                    <span className={`font-medium ${dept.isIT ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {dept.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {dept.users.filter(u => isUserOnline(u.id)).length} online
                      </span>
                      {expandedDepts[dept.name] ? (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {expandedDepts[dept.name] && (
                    <div className="bg-gray-50 dark:bg-gray-900">
                      {dept.users.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => selectUser(user)}
                          className={`w-full flex items-center gap-3 p-3 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-800 ${
                            selectedUser?.id === user.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                          }`}
                        >
                          <div className="relative">
                            <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center text-sm font-medium">
                              {user.fullName.charAt(0)}
                            </div>
                            <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 ${
                              isUserOnline(user.id) ? 'bg-green-500' : 'bg-gray-400'
                            }`} />
                          </div>
                          <div className="flex-1 text-left">
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {user.fullName}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {isUserOnline(user.id) ? 'Online' : 'Offline'}
                            </div>
                          </div>
                          {user.unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                              {user.unreadCount}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {activeTab === 'recent' && (
            <div className="p-2">
              {recentConversations.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <ClockIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Henüz konuşma yok</p>
                </div>
              ) : (
                recentConversations.map((conv) => (
                  <button
                    key={conv.partnerId}
                    onClick={() => selectUser(conv.partner)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 mb-1 ${
                      selectedUser?.id === conv.partnerId ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                    }`}
                  >
                    <div className="relative">
                      <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {conv.partner?.fullName?.charAt(0) || '?'}
                      </div>
                      <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${
                        isUserOnline(conv.partnerId) ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                          {conv.partner?.fullName || 'Bilinmeyen'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDate(conv.lastMessage?.createdAt)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {conv.lastMessage?.content || 'Dosya'}
                      </p>
                    </div>
                    {conv.unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                        {conv.unreadCount}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {activeTab === 'archived' && (
            <div className="p-4">
              {archivedMessages.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <ArchiveBoxIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Arşivlenmiş mesaj yok</p>
                  <p className="text-xs mt-1">Arşivlenen mesajlar 1 haftada silinmez</p>
                </div>
              ) : (
                archivedMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg mb-2 border border-yellow-200 dark:border-yellow-800"
                  >
                    <div className="flex justify-between items-start">
                      <p className="text-sm text-gray-800 dark:text-gray-200">{msg.content}</p>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => toggleArchive(msg.id, e)}
                          className="text-yellow-600 hover:text-yellow-700"
                          title="Arşivden Çıkar"
                        >
                          <ArchiveBoxIconSolid className="h-4 w-4" />
                        </button>
                        {currentUser.role === 'admin' && (
                          <button
                            onClick={() => deleteArchivedMessage(msg.id)}
                            className="text-red-500 hover:text-red-700 ml-1"
                            title="Kalıcı Olarak Sil (Admin)"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(msg.createdAt)} {formatTime(msg.createdAt)}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Chat Rooms Tab */}
          {activeTab === 'rooms' && (
            <div className="p-2">
              {/* Create Room Button */}
              <button
                onClick={() => setShowCreateRoomModal(true)}
                className="w-full flex items-center justify-center gap-2 p-3 mb-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                <PlusIcon className="h-5 w-5" />
                Yeni Oda Oluştur
              </button>

              {chatRooms.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <UserGroupIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Henüz oda yok</p>
                  <p className="text-xs mt-1">Grup sohbeti için oda oluşturun</p>
                </div>
              ) : (
                chatRooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => {
                      // Stop previous room audio if switching rooms
                      if (selectedRoom && selectedRoom.id !== room.id && roomAudioActive) {
                        stopRoomAudio();
                      }
                      setSelectedRoom(room);
                      setSelectedUser(null);
                      fetchRoomMessages(room.id);
                      if (socket) {
                        socket.emit('room:join', { roomId: room.id });
                      }
                      // Start room audio session automatically
                      startRoomAudio(room.id);
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 mb-1 ${
                      selectedRoom?.id === room.id ? 'bg-purple-50 dark:bg-purple-900/30' : ''
                    }`}
                  >
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white">
                      <UserGroupIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                          {room.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {room.memberCount} üye
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {room.lastMessage?.content || room.description || 'Henüz mesaj yok'}
                      </p>
                    </div>
                    {room.unreadCount > 0 && (
                      <span className="bg-purple-500 text-white text-xs rounded-full px-2 py-0.5">
                        {room.unreadCount}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {/* Broadcast Tab */}
          {activeTab === 'broadcast' && (
            <div className="p-2">
              {/* Send Broadcast Button */}
              <button
                onClick={() => setShowBroadcastModal(true)}
                className="w-full flex items-center justify-center gap-2 p-3 mb-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
              >
                <MegaphoneIcon className="h-5 w-5" />
                Duyuru Gönder
              </button>

              {broadcasts.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <MegaphoneIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Henüz duyuru yok</p>
                  <p className="text-xs mt-1">Birim veya tüm kullanıcılara duyuru gönderin</p>
                </div>
              ) : (
                broadcasts.map((broadcast) => (
                  <div
                    key={broadcast.id}
                    className={`p-3 rounded-lg mb-2 border ${
                      broadcast.priority === 'urgent'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                        : broadcast.priority === 'high'
                        ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        broadcast.targetType === 'all' ? 'bg-orange-500' : 'bg-blue-500'
                      } text-white`}>
                        {broadcast.targetType === 'all' ? (
                          <MegaphoneIcon className="h-4 w-4" />
                        ) : (
                          <BuildingOfficeIcon className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            {broadcast.sender?.fullName}
                          </span>
                          {broadcast.priority === 'urgent' && (
                            <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">ACİL</span>
                          )}
                          {broadcast.priority === 'high' && (
                            <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded">ÖNEMLİ</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-800 dark:text-gray-200">{broadcast.content}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400">
                            {broadcast.targetType === 'all' ? 'Tüm Kullanıcılar' : broadcast.targetDepartment?.name}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-400">
                            {formatDate(broadcast.createdAt)} {formatTime(broadcast.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Chat */}
      <div className={`${showMobileChat ? 'flex' : 'hidden md:flex'} flex-1 flex-col w-full`}>
        {/* Room Chat View */}
        {selectedRoom ? (
          <>
            {/* Room Header */}
            <div className="p-2 md:p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-purple-50 dark:bg-purple-900/20">
              <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                {/* Mobile Back Button */}
                <button
                  onClick={() => {
                    if (roomAudioActive) stopRoomAudio();
                    if (socket) socket.emit('room:leave', { roomId: selectedRoom.id });
                    setSelectedRoom(null);
                    setRoomMessages([]);
                  }}
                  className="md:hidden p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <ChevronRightIcon className="h-5 w-5 rotate-180" />
                </button>

                <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                  <UserGroupIcon className="h-5 w-5 md:h-6 md:w-6" />
                </div>
                <div className="min-w-0 flex-shrink">
                  <h3 className="font-semibold text-gray-800 dark:text-white text-sm md:text-base truncate">{selectedRoom.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedRoom.memberCount || selectedRoom.members?.length || 0} üye
                  </p>
                </div>

                {/* Room Audio Controls - Mic & Speaker (always visible) */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Microphone Toggle */}
                  <button
                    onClick={toggleRoomMic}
                    className={`p-2 md:p-2.5 rounded-full transition-all ${
                      roomMicMuted
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        : 'bg-green-500 text-white shadow-lg'
                    }`}
                    title={roomMicMuted ? 'Mikrofonu Aç' : 'Mikrofonu Kapat'}
                  >
                    <MicrophoneIcon className="h-4 w-4 md:h-5 md:w-5" />
                  </button>

                  {/* Speaker Toggle */}
                  <button
                    onClick={toggleRoomSpeaker}
                    className={`p-2 md:p-2.5 rounded-full transition-all ${
                      roomSpeakerMuted
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        : 'bg-blue-500 text-white shadow-lg'
                    }`}
                    title={roomSpeakerMuted ? 'Hoparlörü Aç' : 'Hoparlörü Kapat'}
                  >
                    {roomSpeakerMuted ? (
                      <SpeakerXMarkIcon className="h-4 w-4 md:h-5 md:w-5" />
                    ) : (
                      <SpeakerWaveIcon className="h-4 w-4 md:h-5 md:w-5" />
                    )}
                  </button>

                  {/* Active Participants Count */}
                  {conferenceParticipants.length > 0 && (
                    <span className="hidden md:inline ml-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full">
                      {conferenceParticipants.length} aktif
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                {/* Room Settings Button */}
                <button
                  onClick={openRoomSettings}
                  className="p-2 text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Oda Ayarları"
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                </button>

                <button
                  onClick={() => {
                    // Stop room audio session
                    if (roomAudioActive) {
                      stopRoomAudio();
                    }
                    if (socket) {
                      socket.emit('room:leave', { roomId: selectedRoom.id });
                    }
                    setSelectedRoom(null);
                    setRoomMessages([]);
                  }}
                  className="hidden md:block p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Odadan Çık"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Room Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
              {roomMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.senderId === currentUser.id ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.messageType === 'system' ? (
                    <div className="text-center text-xs text-gray-500 dark:text-gray-400 py-2">
                      {msg.content}
                    </div>
                  ) : (
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        msg.senderId === currentUser.id
                          ? 'bg-purple-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow'
                      }`}
                    >
                      {msg.senderId !== currentUser.id && (
                        <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-1">
                          {msg.sender?.fullName}
                        </p>
                      )}

                      {/* Image Message */}
                      {msg.messageType === 'image' && msg.filePath && (
                        <div className="mb-2">
                          <a
                            href={`${API_URL.replace('/api', '')}${msg.filePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={`${API_URL.replace('/api', '')}${msg.filePath}`}
                              alt={msg.fileName || 'Resim'}
                              className="max-w-full max-h-48 rounded-lg cursor-pointer hover:opacity-90"
                            />
                          </a>
                        </div>
                      )}

                      {/* File Message */}
                      {msg.messageType === 'file' && msg.filePath && (
                        <a
                          href={`${API_URL.replace('/api', '')}${msg.filePath}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 p-2 rounded ${
                            msg.senderId === currentUser.id
                              ? 'bg-purple-700 hover:bg-purple-800'
                              : 'bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500'
                          }`}
                        >
                          <PaperClipIcon className="h-5 w-5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{msg.fileName || 'Dosya'}</p>
                            {msg.fileSize && (
                              <p className={`text-xs ${msg.senderId === currentUser.id ? 'text-purple-200' : 'text-gray-500'}`}>
                                {(msg.fileSize / 1024).toFixed(1)} KB
                              </p>
                            )}
                          </div>
                        </a>
                      )}

                      {/* Text Message */}
                      {(!msg.messageType || msg.messageType === 'text') && (
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      )}

                      <p className={`text-xs mt-1 ${
                        msg.senderId === currentUser.id ? 'text-purple-200' : 'text-gray-500'
                      }`}>
                        {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              {typing && (
                <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                  {typing} yazıyor...
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Room Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              {/* Hidden file input for room */}
              <input
                type="file"
                ref={roomFileInputRef}
                onChange={handleRoomFileUpload}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
              />

              <div className="flex items-center gap-2">
                {/* File Upload Button */}
                <button
                  onClick={() => roomFileInputRef.current?.click()}
                  className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                  title="Dosya Ekle"
                >
                  <PaperClipIcon className="h-5 w-5" />
                </button>

                {/* Image Upload Button */}
                <button
                  onClick={() => {
                    roomFileInputRef.current.accept = 'image/*';
                    roomFileInputRef.current?.click();
                    setTimeout(() => {
                      roomFileInputRef.current.accept = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar";
                    }, 100);
                  }}
                  className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                  title="Resim Ekle"
                >
                  <PhotoIcon className="h-5 w-5" />
                </button>

                {/* Emoji Picker Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowRoomEmojiPicker(!showRoomEmojiPicker)}
                    className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                    title="Emoji"
                  >
                    <FaceSmileIcon className="h-5 w-5" />
                  </button>

                  {/* Room Emoji Picker Popup */}
                  {showRoomEmojiPicker && (
                    <div
                      ref={roomEmojiPickerRef}
                      className="absolute bottom-12 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-72 max-h-80 overflow-y-auto z-50"
                    >
                      {/* Quick Emojis */}
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Hizli Erisim</p>
                        <div className="flex flex-wrap gap-1">
                          {quickEmojis.map((emoji, idx) => (
                            <button
                              key={idx}
                              onClick={() => addRoomEmoji(emoji)}
                              className="text-xl hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Emoji Categories */}
                      {Object.entries(emojiCategories).map(([category, emojis]) => (
                        <div key={category} className="mb-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{category}</p>
                          <div className="flex flex-wrap gap-1">
                            {emojis.slice(0, 20).map((emoji, idx) => (
                              <button
                                key={idx}
                                onClick={() => addRoomEmoji(emoji)}
                                className="text-xl hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    if (socket && selectedRoom) {
                      socket.emit('room:typing:start', { roomId: selectedRoom.id });
                      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                      typingTimeoutRef.current = setTimeout(() => {
                        socket.emit('room:typing:stop', { roomId: selectedRoom.id });
                      }, 2000);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendRoomMessage();
                    }
                  }}
                  placeholder="Odaya mesaj yaz..."
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  onClick={sendRoomMessage}
                  disabled={!newMessage.trim()}
                  className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </>
        ) : !selectedUser ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">
            <div className="text-center">
              <ChatBubbleLeftRightIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Konuşma başlatmak için bir kişi veya oda seçin</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="p-2 md:p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                {/* Mobile Back Button */}
                <button
                  onClick={() => setSelectedUser(null)}
                  className="md:hidden p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <ChevronRightIcon className="h-5 w-5 rotate-180" />
                </button>

                {/* Avatar with Call Button */}
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center text-base md:text-lg font-medium">
                    {selectedUser.fullName.charAt(0)}
                  </div>
                  <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 md:w-3 md:h-3 rounded-full border-2 border-white dark:border-gray-900 ${
                    isUserOnline(selectedUser.id) ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                </div>

                {/* User Info */}
                <div className="min-w-0 flex-shrink">
                  <h3 className="font-semibold text-gray-800 dark:text-white text-sm md:text-base truncate">{selectedUser.fullName}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isUserOnline(selectedUser.id) ? 'Online' : 'Offline'}
                  </p>
                </div>

                {/* Call Button - Next to Avatar */}
                {callState === 'idle' && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isUserOnline(selectedUser.id)) {
                        initiateCall();
                      }
                    }}
                    disabled={!isUserOnline(selectedUser.id)}
                    className={`p-2 md:p-3 rounded-full transition-all shadow-lg active:scale-95 touch-manipulation flex-shrink-0 ${
                      isUserOnline(selectedUser.id)
                        ? 'bg-green-500 hover:bg-green-600 active:bg-green-700 text-white'
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    }`}
                    title={isUserOnline(selectedUser.id) ? 'Sesli Arama Başlat' : 'Kullanıcı çevrimdışı'}
                  >
                    <PhoneIcon className="h-5 w-5 md:h-6 md:w-6" />
                  </button>
                )}
              </div>

              {/* Call Status */}
              <div className="flex items-center gap-2">
                {callState === 'calling' && (
                  <div className="flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 px-4 py-2 rounded-full">
                    <PhoneIcon className="h-5 w-5 text-yellow-600 animate-bounce" />
                    <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400 animate-pulse">Aranıyor...</span>
                    <button
                      onClick={endCall}
                      className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors ml-2"
                      title="Aramayı İptal Et"
                    >
                      <PhoneXMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}

                {callState === 'active' && (
                  <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 px-4 py-2 rounded-full">
                    <PhoneIconSolid className="h-5 w-5 text-green-600 animate-pulse" />
                    <span className="text-sm font-bold text-green-700 dark:text-green-400">
                      {formatCallDuration(callDuration)}
                    </span>
                    <button
                      onClick={toggleMute}
                      className={`p-2 rounded-full transition-colors ${
                        isMuted ? 'bg-red-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                      title={isMuted ? 'Sesi Aç' : 'Sessize Al'}
                    >
                      <MicrophoneIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={toggleSpeaker}
                      className={`p-2 rounded-full transition-colors ${
                        !isSpeakerOn ? 'bg-red-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                      title={isSpeakerOn ? 'Hoparlörü Kapat' : 'Hoparlörü Aç'}
                    >
                      {isSpeakerOn ? <SpeakerWaveIcon className="h-5 w-5" /> : <SpeakerXMarkIcon className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={endCall}
                      className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                      title="Aramayı Bitir"
                    >
                      <PhoneXMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Hidden audio element for remote audio - with playsInline for mobile */}
            <audio ref={remoteAudioRef} autoPlay playsInline />

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.senderId === currentUser.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 relative group ${
                      msg.senderId === currentUser.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow'
                    }`}
                  >
                    {/* Archive button - always visible */}
                    <button
                      onClick={(e) => toggleArchive(msg.id, e)}
                      className={`absolute -top-3 -right-3 p-1.5 rounded-full shadow-md hover:scale-110 transition-transform ${
                        msg.isArchived
                          ? 'bg-yellow-500 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-500 dark:text-gray-300 border border-gray-300 dark:border-gray-500'
                      }`}
                      title={msg.isArchived ? 'Arşivden Çıkar' : 'Arşivle (1 haftada silinmez)'}
                    >
                      {msg.isArchived ? (
                        <ArchiveBoxIconSolid className="h-4 w-4" />
                      ) : (
                        <ArchiveBoxIcon className="h-4 w-4" />
                      )}
                    </button>

                    {msg.messageType === 'image' ? (
                      <img
                        src={`${SOCKET_URL}${msg.filePath}`}
                        alt={msg.fileName}
                        className="max-w-full rounded cursor-pointer"
                        onClick={() => window.open(`${SOCKET_URL}${msg.filePath}`, '_blank')}
                      />
                    ) : msg.messageType === 'file' ? (
                      <a
                        href={`${SOCKET_URL}${msg.filePath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 underline"
                      >
                        <PaperClipIcon className="h-4 w-4" />
                        {msg.fileName}
                      </a>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                    <div className={`text-xs mt-1 flex items-center gap-1 ${
                      msg.senderId === currentUser.id ? 'text-blue-200' : 'text-gray-500'
                    }`}>
                      {formatTime(msg.createdAt)}
                      {renderStatus(msg)}
                      {msg.isArchived && (
                        <ArchiveBoxIconSolid className="h-3 w-3 text-yellow-400 ml-1" title="Arşivlendi" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {typing && (
                <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                  {typing} yazıyor...
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Dosya Ekle"
                >
                  <PaperClipIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => {
                    fileInputRef.current.accept = 'image/*';
                    fileInputRef.current?.click();
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Resim / Ekran Görüntüsü"
                >
                  <PhotoIcon className="h-5 w-5" />
                </button>

                {/* Emoji Button & Picker */}
                <div className="relative" ref={emojiPickerRef}>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${
                      showEmojiPicker ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                    title="Emoji Ekle 😊"
                  >
                    <FaceSmileIcon className="h-5 w-5" />
                  </button>

                  {/* Emoji Picker Panel */}
                  {showEmojiPicker && (
                    <div className="absolute bottom-12 left-0 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                      {/* Quick Emojis */}
                      <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">⚡ Hızlı Erişim</p>
                        <div className="flex flex-wrap gap-1">
                          {quickEmojis.map((emoji, idx) => (
                            <button
                              key={idx}
                              onClick={() => addEmoji(emoji)}
                              className="w-8 h-8 text-lg hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-all hover:scale-125 flex items-center justify-center"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Categories */}
                      <div className="max-h-64 overflow-y-auto p-2">
                        {Object.entries(emojiCategories).map(([category, emojis]) => (
                          <div key={category} className="mb-3">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 sticky top-0 bg-white dark:bg-gray-800 py-1">
                              {category}
                            </p>
                            <div className="flex flex-wrap gap-0.5">
                              {emojis.slice(0, 30).map((emoji, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => addEmoji(emoji)}
                                  className="w-7 h-7 text-base hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-all hover:scale-110 flex items-center justify-center"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    handleTyping();
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="Mesaj yaz... 💬"
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                💡 Arşivlenmemiş mesajlar 1 hafta sonra otomatik silinir
              </p>
            </div>
          </>
        )}
      </div>

      {/* Incoming Call Modal */}
      {callState === 'incoming' && incomingCall && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-80 text-center animate-pulse">
            {/* Caller Avatar */}
            <div className="relative inline-block mb-4">
              <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                {incomingCall.callerName?.charAt(0) || '?'}
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                <PhoneIcon className="h-5 w-5 text-white" />
              </div>
            </div>

            {/* Caller Info */}
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
              {incomingCall.callerName}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 animate-pulse">
              Sizi arıyor...
            </p>

            {/* Action Buttons */}
            <div className="flex justify-center gap-6">
              {/* Reject Button */}
              <button
                onClick={rejectCall}
                className="w-16 h-16 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
                title="Reddet"
              >
                <PhoneXMarkIcon className="h-8 w-8" />
              </button>

              {/* Accept Button */}
              <button
                onClick={acceptCall}
                className="w-16 h-16 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110 animate-pulse"
                title="Cevapla"
              >
                <PhoneIcon className="h-8 w-8" />
              </button>
            </div>

            {/* Instruction Text */}
            <p className="text-xs text-gray-400 mt-6">
              Aramayı cevaplamak için yeşil butona basın
            </p>
          </div>
        </div>
      )}

      {/* Fixed Notification Box at Bottom */}
      {pendingNotifications.length > 0 && (
        <div className="fixed bottom-4 right-4 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          <div className="bg-red-500 text-white px-4 py-2 flex items-center justify-between">
            <span className="font-medium flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5" />
              Yeni Mesajlar ({pendingNotifications.length})
            </span>
            <button
              onClick={() => setPendingNotifications([])}
              className="hover:bg-red-600 rounded p-1"
              title="Tümünü Kapat"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {pendingNotifications
              .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
              .map((notif, index) => (
                <div
                  key={notif.id}
                  onClick={() => {
                    setSelectedUser({ id: notif.senderId, fullName: notif.senderName });
                    setPendingNotifications(prev => prev.filter(n => n.senderId !== notif.senderId));
                  }}
                  className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    index !== pendingNotifications.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {notif.senderName.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                          {notif.senderName}
                        </p>
                        <span className="text-xs text-gray-400 ml-2">
                          {formatTime(notif.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {notif.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Incoming Conference Call Modal */}
      {incomingConference && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            {/* Conference Icon */}
            <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse shadow-lg">
              <UserGroupIcon className="h-12 w-12 text-white" />
            </div>

            {/* Caller Info */}
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
              Grup Araması
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {incomingConference.initiatorName}
            </p>
            <p className="text-gray-500 dark:text-gray-400 mb-6 animate-pulse">
              Sizi konferansa davet ediyor...
            </p>

            {/* Action Buttons */}
            <div className="flex justify-center gap-6">
              {/* Reject Button */}
              <button
                onClick={declineConference}
                className="w-16 h-16 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
                title="Reddet"
              >
                <PhoneXMarkIcon className="h-8 w-8" />
              </button>

              {/* Accept Button */}
              <button
                onClick={joinConference}
                className="w-16 h-16 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110 animate-pulse"
                title="Katıl"
              >
                <PhoneIcon className="h-8 w-8" />
              </button>
            </div>

            {/* Instruction Text */}
            <p className="text-xs text-gray-400 mt-6">
              Konferansa katılmak için yeşil butona basın
            </p>
          </div>
        </div>
      )}

      {/* Create Room Modal */}
      {showCreateRoomModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-96 max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5" />
                Yeni Sohbet Odası
              </h3>
              <button
                onClick={() => {
                  setShowCreateRoomModal(false);
                  setNewRoomName('');
                  setNewRoomDescription('');
                  setSelectedRoomMembers([]);
                }}
                className="hover:bg-purple-700 rounded p-1"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Oda Adı *
                </label>
                <input
                  type="text"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder="Örn: Proje Ekibi"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Açıklama
                </label>
                <input
                  type="text"
                  value={newRoomDescription}
                  onChange={(e) => setNewRoomDescription(e.target.value)}
                  placeholder="Oda açıklaması..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Üye Ekle ({selectedRoomMembers.length} seçili)
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg">
                  {allUsers.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoomMembers.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRoomMembers([...selectedRoomMembers, user.id]);
                          } else {
                            setSelectedRoomMembers(selectedRoomMembers.filter(id => id !== user.id));
                          }
                        }}
                        className="rounded text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{user.fullName}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateRoomModal(false);
                  setNewRoomName('');
                  setNewRoomDescription('');
                  setSelectedRoomMembers([]);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={createChatRoom}
                disabled={!newRoomName.trim()}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Oda Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast Modal */}
      {showBroadcastModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-96 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-orange-600 text-white px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <MegaphoneIcon className="h-5 w-5" />
                Duyuru Gönder
              </h3>
              <button
                onClick={() => {
                  setShowBroadcastModal(false);
                  setBroadcastContent('');
                  setBroadcastTarget('all');
                  setBroadcastDeptId('');
                  setBroadcastPriority('normal');
                }}
                className="hover:bg-orange-700 rounded p-1"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Hedef Kitle
                </label>
                <select
                  value={broadcastTarget}
                  onChange={(e) => setBroadcastTarget(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange-500"
                >
                  {['admin', 'it', 'idari_isler'].includes(currentUser.role) && (
                    <option value="all">Tüm Kullanıcılar</option>
                  )}
                  <option value="department">Birim</option>
                </select>
              </div>

              {broadcastTarget === 'department' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Birim Seç
                  </label>
                  <select
                    value={broadcastDeptId}
                    onChange={(e) => setBroadcastDeptId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Birim seçin...</option>
                    {departments.map((dept) => (
                      <option key={dept.name} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Öncelik
                </label>
                <select
                  value={broadcastPriority}
                  onChange={(e) => setBroadcastPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange-500"
                >
                  <option value="normal">Normal</option>
                  <option value="high">Önemli</option>
                  <option value="urgent">Acil</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Duyuru Mesajı *
                </label>
                <textarea
                  value={broadcastContent}
                  onChange={(e) => setBroadcastContent(e.target.value)}
                  placeholder="Duyuru içeriğini yazın..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange-500 resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowBroadcastModal(false);
                  setBroadcastContent('');
                  setBroadcastTarget('all');
                  setBroadcastDeptId('');
                  setBroadcastPriority('normal');
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={sendBroadcast}
                disabled={!broadcastContent.trim() || (broadcastTarget === 'department' && !broadcastDeptId)}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Duyuru Gönder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Room Settings Modal */}
      {showRoomSettingsModal && roomDetails && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[450px] max-h-[85vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Cog6ToothIcon className="h-5 w-5" />
                Oda Ayarları - {roomDetails.name}
              </h3>
              <button
                onClick={() => {
                  setShowRoomSettingsModal(false);
                  setRoomDetails(null);
                  setAddMemberIds([]);
                }}
                className="hover:bg-purple-700 rounded p-1"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Room Info */}
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Oluşturan:</span> {roomDetails.creator?.fullName}
                </p>
                {roomDetails.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <span className="font-medium">Açıklama:</span> {roomDetails.description}
                  </p>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  <span className="font-medium">Rolünüz:</span> {roomDetails.myRole === 'admin' ? 'Yönetici' : 'Üye'}
                </p>
              </div>

              {/* Current Members */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mevcut Üyeler ({roomDetails.members?.length || 0})
                </label>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-40 overflow-y-auto">
                  {roomDetails.members?.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                          {member.fullName?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{member.fullName}</span>
                          {member.id === roomDetails.createdById && (
                            <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded">
                              Yönetici
                            </span>
                          )}
                          {member.id === currentUser.id && (
                            <span className="ml-1 text-xs text-gray-500">(Sen)</span>
                          )}
                        </div>
                      </div>
                      {roomDetails.myRole === 'admin' && member.id !== currentUser.id && member.id !== roomDetails.createdById && (
                        <button
                          onClick={() => removeMemberFromRoom(member.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Üyeyi Çıkar"
                        >
                          <UserMinusIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Add New Members - Only for room admin */}
              {roomDetails.myRole === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yeni Üye Ekle
                  </label>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-32 overflow-y-auto">
                    {allUsers
                      .filter(u => !roomDetails.members?.some(m => m.id === u.id))
                      .map((user) => (
                        <label
                          key={user.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={addMemberIds.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAddMemberIds([...addMemberIds, user.id]);
                              } else {
                                setAddMemberIds(addMemberIds.filter(id => id !== user.id));
                              }
                            }}
                            className="rounded text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{user.fullName}</span>
                        </label>
                      ))}
                    {allUsers.filter(u => !roomDetails.members?.some(m => m.id === u.id)).length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 p-3 text-center">
                        Eklenecek kullanıcı kalmadı
                      </p>
                    )}
                  </div>
                  {addMemberIds.length > 0 && (
                    <button
                      onClick={addMembersToRoom}
                      className="mt-2 w-full px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                    >
                      <UserPlusIcon className="h-4 w-4" />
                      {addMemberIds.length} Üye Ekle
                    </button>
                  )}
                </div>
              )}

              {/* Danger Zone - Delete Room (Admin Only) */}
              {roomDetails.myRole === 'admin' && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Tehlikeli Bölge</h4>
                  <button
                    onClick={deleteRoom}
                    className="w-full px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Odayı Sil
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                    Oda silinir ancak mesajlar arşivlenir
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 flex justify-end">
              <button
                onClick={() => {
                  setShowRoomSettingsModal(false);
                  setRoomDetails(null);
                  setAddMemberIds([]);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
